import type { ApplyToFramesResponse } from "@/types/onboarding";

// The apply run's phases (display order lives in applyPhases.ts).
export type ApplyPhase = "fetch" | "render" | "blocks";

export interface ApplyStreamEvent {
  progress?: number;
  phase?: ApplyPhase;
  // Sub-progress within the phase (the backend emits render batches done / total).
  current?: number;
  total?: number;
  result?: ApplyToFramesResponse;
  done?: boolean;
  error?: string;
}

// Live apply progress as published to the query cache by useApplyToFrames: the
// monotonic overall pct plus, once the render phase reports it, that phase's
// sub-progress (`current` of `total` render batches).
export interface ApplyProgress {
  pct: number;
  current?: number;
  total?: number;
}

// Tolerant mapping from whatever the backend labels a phase to our three phases.
// Adjust here once the real stream field values are confirmed.
const PHASE_ALIASES: Record<string, ApplyPhase> = {
  fetch: "fetch",
  fetching: "fetch",
  setup: "fetch",
  load: "fetch",
  loading: "fetch",
  render: "render",
  rendering: "render",
  frame: "render",
  frames: "render",
  block: "blocks",
  blocks: "blocks",
  saved_blocks: "blocks",
};

/**
 * Splits a raw SSE text buffer into complete event payloads.
 *
 * SSE frames are separated by a blank line. Within a frame we keep only `data:`
 * lines (joined with "\n" per the spec) and ignore `event:` / `id:` / comment
 * lines. The trailing partial frame (not yet terminated by a blank line) is
 * returned as `rest`, to be prepended to the next chunk.
 *
 * This is the single place that understands the SSE wire framing, so it can be
 * unit-tested independently of the network read loop.
 */
export function parseSSEBuffer(buffer: string): {
  events: string[];
  rest: string;
} {
  const normalized = buffer.replace(/\r\n/g, "\n");
  const frames = normalized.split("\n\n");
  const rest = frames.pop() ?? "";

  const events: string[] = [];
  for (const frame of frames) {
    const data = frame
      .split("\n")
      .filter((line) => line.startsWith("data:"))
      .map((line) => line.slice("data:".length).replace(/^ /, ""))
      .join("\n")
      .trim();
    if (data) events.push(data);
  }

  return { events, rest };
}

function toNumber(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
}

function clampPercent(value: number): number {
  if (value < 0) return 0;
  if (value > 100) return 100;
  return value;
}

function isApplyResponseLike(value: unknown): value is ApplyToFramesResponse {
  return (
    !!value &&
    typeof value === "object" &&
    "frames" in value &&
    typeof (value as Record<string, unknown>).frames === "object" &&
    (value as Record<string, unknown>).frames !== null
  );
}

function extractPhase(event: Record<string, unknown>): ApplyPhase | undefined {
  const raw = event.phase ?? event.stage ?? event.step;
  if (typeof raw !== "string") return undefined;
  return PHASE_ALIASES[raw.trim().toLowerCase()];
}

function extractProgress(event: Record<string, unknown>): number | undefined {
  // Assume percentages are 0–100. Prefer an explicit figure (the backend sends
  // `pct`); otherwise derive it from a completed/total pair.
  const explicit =
    toNumber(event.pct) ??
    toNumber(event.progress) ??
    toNumber(event.percent) ??
    toNumber(event.percentage);
  if (explicit !== undefined) return clampPercent(explicit);

  const total = toNumber(event.total);
  const completed =
    toNumber(event.completed) ??
    toNumber(event.current) ??
    toNumber(event.index);
  if (total !== undefined && total > 0 && completed !== undefined) {
    return clampPercent((completed / total) * 100);
  }

  return undefined;
}

// Sub-progress within a phase: a `total` plus a `current`/`completed`/`index`
// figure. Go's omitempty drops a zero `current`, so a missing figure means none
// done yet.
function extractCount(
  event: Record<string, unknown>,
): Pick<ApplyStreamEvent, "current" | "total"> {
  const total = toNumber(event.total);
  if (total === undefined || total <= 0) return {};
  const current =
    toNumber(event.current) ??
    toNumber(event.completed) ??
    toNumber(event.index) ??
    0;
  return { current, total };
}

/**
 * Interprets a single parsed SSE event object for the apply-to-frames stream.
 *
 * This is the ONLY place that encodes assumptions about the backend event shape,
 * so adjusting to the real contract means editing just this function. It tolerates
 * several plausible shapes (see the plan / PR description):
 *   - progress:  `{ type: "progress", progress|percent|percentage }` or a
 *                `completed`/`current`/`index` + `total` pair.
 *   - terminal:  `{ type: "result"|"done"|"complete", ... }`, a wrapped `result`
 *                / `data`, or a bare payload that looks like an ApplyToFramesResponse.
 *   - error:     `{ type: "error" }` or any `{ error: string }`.
 */
export function extractApplyStreamEvent(obj: unknown): ApplyStreamEvent {
  if (!obj || typeof obj !== "object") return {};
  const event = obj as Record<string, unknown>;
  const type = typeof event.type === "string" ? event.type : undefined;

  // Error event → surface a message for the caller to throw.
  if (type === "error" || typeof event.error === "string") {
    const message =
      typeof event.error === "string" && event.error.trim()
        ? event.error
        : "Apply to frames failed";
    return { error: message };
  }

  const phase = extractPhase(event);

  // A progress event never carries the terminal result, even if it happens to
  // include a partial `frames` object — treat it strictly as progress (+ phase).
  if (type === "progress") {
    const progress = extractProgress(event);
    return {
      ...(progress !== undefined ? { progress } : {}),
      ...(phase ? { phase } : {}),
      ...extractCount(event),
    };
  }

  const terminalType =
    type === "result" || type === "done" || type === "complete";

  // Terminal result: a wrapped `result` / `data`, or (for an untyped or
  // result-typed frame) a payload that itself looks like an ApplyToFramesResponse.
  if (isApplyResponseLike(event.result)) {
    return { result: event.result, done: true, progress: 100 };
  }
  if (isApplyResponseLike(event.data)) {
    return { result: event.data, done: true, progress: 100 };
  }
  if ((terminalType || type === undefined) && isApplyResponseLike(event)) {
    return { result: event, done: true, progress: 100 };
  }

  // Terminal signal without an inline body (the result arrived separately).
  if (terminalType || event.done === true) {
    return { done: true, progress: 100 };
  }

  // Fallback: any other event that carries a progress figure and/or a phase
  // (e.g. a standalone `{ phase: "render" }` phase-change frame).
  const progress = extractProgress(event);
  return {
    ...(progress !== undefined ? { progress } : {}),
    ...(phase ? { phase } : {}),
    ...extractCount(event),
  };
}
